import { baseJoystickZone } from './questCardModel';
import { hudMenuLayout, hudMenuRects } from './hudMenuLayout';
import { battleCoverage } from './hudCoverage';
// 화면 좌표의 실제 터치 사각형. 레이아웃 변경 때만 계산하며 고정 스텝에서는 호출하지 않는다.
export interface HudRect { x: number; y: number; w: number; h: number }
export interface HudInsets { left: number; top: number; right: number; bottom: number }
const NO_INSETS: HudInsets = { left: 0, top: 0, right: 0, bottom: 0 };

export function protectedPlayRect(w: number, h: number, topHud = 112, bottomControls = 104): HudRect {
  return { x: w / 3, y: topHud, w: w / 3, h: Math.max(0, h - topHud - bottomControls) };
}
export function hudRectsOverlap(a: HudRect, b: HudRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** 상단 중앙은 적·보스 체력 전용. 목표는 왼쪽 1/3 안에서 완전한 문장을 줄바꿈한다. */
export function objectiveHudLayout(w: number, _leftRight = 0, _leftBottom = 0, _rightWidth = 0, compact = false): HudRect {
  return { x: 8, y: w >= 1000 ? 136 : 112, w: Math.min(352, w / 3 - 16), h: compact ? OBJECTIVE_COMPACT_H : w < 600 ? 112 : 64 };
}

/** 레이드 + 짧은 화면(폰 가로)에서 목표 카드를 제목 한 줄로 접는 높이 */
export const OBJECTIVE_COMPACT_H = 28;
/** 접는 조건: 레이드 장면이고 화면 높이 420px 이하(폰 가로). 일반 사냥 HUD는 그대로 */
export function objectiveCompact(h: number, raid: boolean): boolean { return raid && h <= 420; }

export function dungeonAutoButtonLayout(objectiveBottom: number): HudRect {
  return { x: 8, y: Math.max(116, Math.ceil(objectiveBottom + 8)), w: 88, h: 44 };
}

/** 상단 HUD 띠 높이(체력·초상·적/보스 체력·우상단 묶음). 왼쪽 띠에 자리가 없을 때 알림은 이 띠 바로 아래 가운데로 올린다 */
export const HUD_TOP_BAND = 112;
/** 화면 아래·위 여백 */
const EDGE = 6;
/** 알약 양끝 금색 마름모 장식이 박스 밖으로 나오는 폭 */
export const AUTO_STATUS_DIAMOND = 7;

/**
 * 자동 이동·종료 이유는 콤보 아래 왼쪽 띠에서 줄바꿈한다. 멈춤 버튼은 원래 크기를 유지한다.
 * autoButtonBottom: 던전 「자동/진행 중」 버튼의 아래 끝(없으면 0). 콤보가 숨어 있을 때 알약이 176에 올라가 그 버튼(184~228)에 글자가 가려졌다(PR #211 화면 확인)
 * h·pillH(알약 실제 높이)를 주면 화면 안으로 맞춘다: 왼쪽 띠 아래에 들어가지 않으면(568×320 자동 이동 + 콤보에서 y 389) 상단 HUD 띠 바로 아래 가운데로 올리고,
 * 그래도 넘치면 화면 아래 끝에 붙인다(PR #211 리뷰 P2).
 */
export function autoStatusHudLayout(w: number, comboBottom: number, autoButtonBottom = 0, h = Infinity, pillH = 0) {
  const width = Math.min(360, w / 3 - 16), stacked = w < 600;
  const y = Math.max(176, Math.ceil(comboBottom + 8), autoButtonBottom > 0 ? Math.ceil(autoButtonBottom + 8) : 0);
  if (y + pillH <= h - EDGE) return { x: 8, y, w: width, stacked, centered: false };
  return { x: Math.round((w - width) / 2), y: Math.max(0, Math.min(HUD_TOP_BAND + EDGE, h - EDGE - pillH)), w: width, stacked, centered: true };
}

/**
 * 방 요령 토스트 top(왼쪽 띠): 목표 아래 자동 버튼보다 아래, 자동 이동 알약(auto-status)이 같은 띠에 보이면 그 알약보다도 아래(PR #211 리뷰 P2: 844×390에서 알약 y≈257과 요령 y=238이 4.5초 겹쳤다).
 * autoStatusBottom은 알약의 실제 아래 끝(보이지 않으면 0). 알약 높이는 글자 줄바꿈에 따라 달라 실제 사각형을 받는다.
 */
export function roomTipTop(objectiveBottom: number, autoStatusBottom = 0): number {
  const auto = dungeonAutoButtonLayout(objectiveBottom);
  return Math.max(auto.y + auto.h + 10, autoStatusBottom > 0 ? Math.ceil(autoStatusBottom + 10) : 0);
}

/** 요령 토스트 배율: 기본 배율(base)로 화면 아래(여백 6px)에 들어가면 그대로, 아니면 남은 높이에 맞춰 줄이되 0.6 아래로는 줄이지 않는다. tipH는 배율 전 높이 */
export const ROOM_TIP_MIN_SCALE = 0.6;
export function roomTipScale(top: number, viewH: number, tipH: number, base: number): number {
  if (!(tipH > 0) || top + tipH * base <= viewH - EDGE) return base;
  return Math.max(ROOM_TIP_MIN_SCALE, Math.min(base, (viewH - EDGE - top) / tipH));
}

/**
 * 요령 토스트 자리(PR #211 리뷰 P2). 줄이기만으로 끝내지 않고 자리를 바꾼다:
 *  1) 왼쪽 띠(자동 버튼·같은 띠의 알약 아래)에 기본 배율로 들어가면 그대로
 *  2) 머리글을 숨긴 작은 모양(tipHCompact)으로 0.7배 이상이면 왼쪽 띠에 그대로
 *  3) 아니면 상단 HUD 띠 바로 아래 가운데(가운데 1/3 폭 안). 알약이 가운데로 올라와 있으면 그 아래
 * tipW·tipH·tipHCompact는 배율 전 크기, status는 보이는 알약 사각형(없으면 null). 돌려준 x·y는 transform-origin 0 0 기준 왼쪽 위.
 */
export function roomTipLayout(w: number, h: number, objectiveBottom: number, status: HudRect | null, tipW: number, tipH: number, tipHCompact: number, base: number) {
  // 왼쪽 띠 알약은 x 8에 붙는다(autoStatusHudLayout). 가운데로 올라간 알약은 왼쪽 띠를 비운다
  const statusInLeft = !!status && status.x < 8 + 2;
  const top = roomTipTop(objectiveBottom, statusInLeft && status ? status.y + status.h : 0);
  if (top + tipH * base <= h - EDGE) return { x: 8, y: top, scale: base, compact: false, centered: false };
  const sc = (h - EDGE - top) / tipHCompact;
  if (tipHCompact > 0 && sc >= Math.min(base, 0.7)) return { x: 8, y: top, scale: Math.min(base, sc), compact: true, centered: false };
  const scale = Math.max(ROOM_TIP_MIN_SCALE, Math.min(base, (w / 3 - 8) / tipW));
  const statusCentered = !!status && !statusInLeft;
  let y = statusCentered && status ? Math.ceil(status.y + status.h + 10) : HUD_TOP_BAND + EDGE;
  let compact = false;
  if (y + tipH * scale > h - EDGE) {
    compact = true;
    if (y + tipHCompact * scale > h - EDGE) y = Math.max(0, Math.floor(h - EDGE - tipHCompact * scale));
  }
  return { x: Math.round(w / 2 - (tipW * scale) / 2), y, scale, compact, centered: true };
}

/**
 * 콤보 숫자·COMBO·칭찬을 하나의 왼쪽 띠 안에 둔다. 자동 버튼과 세로로 나누거나 가로로 나란히 둔다.
 * 좁고 짧은 가로 화면(568×320)은 목표 아래로 쌓으면 화면 밖(284~381)이라 자동 버튼 오른쪽에 둔다(PR #211 리뷰 P2).
 */
export function comboHudLayout(w: number, h: number, count: number) {
  const stacked = w < 600;
  const obj = objectiveHudLayout(w);
  const digits = String(Math.max(0, count)).length;
  const calc = (width: number, small: boolean) => {
    // 2026-10-27 대표님 "콤보 화면보다 액션": 콤보 글자를 줄여 왼쪽 띠가 전투 장면을 덜 가리게(PC 64→44px)
    const wordFont = stacked && !small ? 18 : h < 500 ? 14 : 18;
    // 숫자 확대(1.18)·글자 기울기·테두리까지 띠 안에 여백을 남긴다.
    const numberSpace = width - (stacked ? 16 : 6 * wordFont + 14);
    const numberFont = Math.min(stacked && !small ? 40 : h < 500 ? 32 : 44, numberSpace / (digits * .85 * 1.18));
    const praiseFont = Math.min(20, (width - 16) / 8);
    const rowHeight = Math.ceil((stacked ? numberFont + wordFont : Math.max(numberFont, wordFont)) * 1.2);
    return { wordFont, numberFont, praiseFont, rowHeight, height: rowHeight + Math.ceil(praiseFont * 1.2) + 4 };
  };
  const width = w / 3 - (stacked ? 24 : 124);
  const y = obj.y + obj.h + (stacked ? 60 : 8);
  const c = calc(width, false);
  if (!stacked || y + c.height <= h - EDGE) {
    return { x: stacked ? 8 : 108, y, w: width, h: c.height, stacked, numberFont: c.numberFont, wordFont: c.wordFont, praiseFont: c.praiseFont, rowHeight: c.rowHeight };
  }
  const auto = dungeonAutoButtonLayout(obj.y + obj.h);
  const bx = auto.x + auto.w + 8, bw = obj.x + obj.w - bx;
  const b = calc(bw, true);
  return { x: bx, y: auto.y, w: bw, h: b.height, stacked, numberFont: b.numberFont, wordFont: b.wordFont, praiseFont: b.praiseFont, rowHeight: b.rowHeight };
}

/**
 * 터치 HUD(UX-FLOW 3절). 가로 화면은 오른손 엄지 호 배치: 오른쪽 아래 구석의 큰 공격 버튼(지름 화면 높이 18% 이상, 일반 버튼의 1.4배 이상),
 * 그 둘레 안쪽 호에 회피·스킬 2칸, 바깥 호에 스킬 3칸과 맨 위 각성, 바닥 줄 왼쪽으로 스킬 1칸·물약 2칸·쪽 표시. 호가 화면에 들어가지 않으면(작은 가로 화면) 격자로 돌아간다.
 * 세로 화면은 2열 격자. 빈 슬롯도 자리를 보존해 페이지 전환 때 손 위치가 변하지 않는다.
 */
export function touchHudLayout(w: number, h: number, scale: number, safe: HudInsets = NO_INSETS) {
  if (w >= h) {
    const arc = touchArcLayout(w, h, scale, safe);
    // 폰 가로(C8-8 면적 예산): 엄지 호(큰 공격 버튼)는 전투 영역(가장자리 띠 안쪽)을 PHONE_BATTLE_BUDGET 이하로 덮을 때만 쓴다.
    // 호는 왼쪽으로 펼쳐져 폭이 좁은 가로 화면(700×390·640×360)에서는 예산을 넘는다 → 오른쪽 가장자리에 붙는 격자(예전에도 이 크기는 격자)
    if (arc && (!phoneLandscape(w, h) || touchBattleCoverage(arc, w, h) <= PHONE_BATTLE_BUDGET)) return arc;
  }
  return touchGridLayout(w, h, scale, safe);
}

type TouchHudLayout = ReturnType<typeof touchGridLayout>;

/** 조작 12칸 + 각성 게이지 사각형(면적 예산·겹침 측정용) */
export function touchControlRects(p: TouchHudLayout): HudRect[] {
  return [...p.skills, p.attack, p.dodge, p.awaken, p.page, ...p.potions, p.gauge];
}
/** 터치 조작이 전투 영역을 덮는 비율(hudCoverage.battleCoverage) */
export function touchBattleCoverage(p: TouchHudLayout, w: number, h: number): number {
  return battleCoverage(touchControlRects(p), w, h);
}

function joystickRect(w: number, h: number, safe: HudInsets, margin: number): HudRect {
  const j = Math.min(128, w / 3 - safe.left - margin * 2);
  return { x: safe.left + margin, y: h - safe.bottom - margin - j, w: j, h: j };
}

/**
 * 엄지 고리 배치(2026-10-27 HUD 정리, 전투 시안 A). 모든 조작이 둥근 버튼이라 간격은 중심 사이 거리(지름 + 10px)로 잰다
 * (버튼 뿌리에 border-radius 50%가 있어 눌리는 자리도 원이다).
 *  - 공격: 오른쪽 아래 구석, 지름 = 스킬의 1.7배
 *  - 안쪽 고리(공격에서 엄지가 바로 미끄러지는 자리): 회피 180° · 스킬1 135° · 스킬2 90°
 *  - 바깥 고리 다섯 칸을 22.5° 간격으로: 스킬3 180° · 스킬4 157.5° · 스킬5 135° · 스킬6 112.5° · 각성 90°(맨 위)
 *  - 물약 2칸·쪽 표시: 바깥 고리 왼쪽 아래 줄(조이스틱 입력 영역과 화면 가운데 보호 영역 사이 띠)
 *  - 각성 게이지: 각성 버튼 바로 위 짧은 막대
 * 자리가 모자라면(작은 가로 화면) 지름을 2px씩 줄여 다시 재고, 44px로도 안 되면 null(격자로 돌아간다).
 */
function touchArcLayout(w: number, h: number, scale: number, safe: HudInsets): TouchHudLayout | null {
  const gap = 10, margin = 14;
  const joystick = joystickRect(w, h, safe, margin);
  const protectedArea = protectedPlayRect(w, h);
  const deg = Math.PI / 180;
  const compact = phoneLandscape(w, h);
  // 폰 가로(C8-8): 지름 상한을 화면 높이 15% → 12.5%로(844×390에서 58 → 48px). 호 전체가 약 266px → 214px 정사각으로 줄어
  // 오른쪽 아래가 덜 덮이고, 700×390·640×360처럼 예전에는 격자(3×4)로 떨어지던 작은 가로 화면도 호에 들어간다
  const top = Math.round(Math.max(44, Math.min(62 * Math.max(0.8, scale), h * (compact ? PHONE_ARC_FRAC : 0.15))));
  for (let d = top; d >= 44; d -= 2) {
    const ra = Math.round(d * 0.85);
    const ax = w - safe.right - margin - ra, ay = h - safe.bottom - margin - ra;
    const at = (r: number, a: number, size = d): HudRect => ({ x: ax + Math.cos(a * deg) * r - size / 2, y: ay - Math.sin(a * deg) * r - size / 2, w: size, h: size });
    // 고리 반지름: 공격 가장자리에서 gap, 같은 고리 이웃 사이 지름 + gap(현의 길이)
    let r1 = Math.max(ra + gap + d / 2, (d + gap) / (2 * Math.sin(22.5 * deg)));
    let r2 = Math.max(r1 + d + gap, (d + gap) / (2 * Math.sin(11.25 * deg)));
    if (!compact && h > 500) {
      // 태블릿·PC 터치(높이 500 초과; 폰 가로 932×430 등은 미니맵 자리 때문에 예전 배치): 원이 아니라 바깥 사각형끼리도 겹치지 않게 반지름을 넓힌다(QA 2026-11-03 태블릿 1024x768 공격 ↔ 스킬 306px²).
      // 135° 대각 이웃은 가로·세로 중 한 축으로 (공격 반지름 + 스킬 반폭) 이상 떨어져야 하고, 바깥 고리는 22.5° 이웃의 두 축 중 하나가 지름 이상 떨어져야 한다
      r1 = Math.max(r1, Math.ceil((ra + d / 2) * Math.SQRT2) + 2);
      r2 = Math.max(r2, r1 + Math.ceil(d * Math.SQRT2) + 2, Math.ceil(d * 3.1));
    }
    const attack: HudRect = { x: ax - ra, y: ay - ra, w: ra * 2, h: ra * 2 };
    const dodge = at(r1, 180), s1 = at(r1, 135), s2 = at(r1, 90);
    const s3 = at(r2, 180), s4 = at(r2, 157.5), s5 = at(r2, 135), s6 = at(r2, 112.5), awaken = at(r2, 90);
    const gauge: HudRect = { x: awaken.x - 24, y: awaken.y - 22, w: d + 48, h: 16 };
    // 아래 줄: 바깥 고리 맨 왼쪽(스킬3)에서 왼쪽으로 물약 2 → 쪽 표시. 덜 누르는 칸이라 44px
    const small = 44;
    const rowY = ay - small / 2;
    let x = s3.x - gap;
    const row = [0, 1, 2].map(() => { x -= small; const r = { x, y: rowY, w: small, h: small }; x -= gap; return r; });
    const base: TouchHudLayout = {
      size: d, skills: [s1, s2, s3, s4, s5, s6], page: row[2], potions: [row[0], row[1]],
      dodge, awaken, attack, joystick, gauge,
    };
    if (!compact) { if (touchArcFits(base, w, h, safe, protectedArea)) return base; continue; }
    const layout = phoneSmallRow(base, w, h, safe, protectedArea, s3, gauge, small, gap);
    if (layout) return layout;
  }
  return null;
}

/** 폰 가로(짧은 가로 화면): 게임이 세로 기기도 가로로 돌려 그리므로 390×700·360×640 세로 기기도 여기에 든다 */
export function phoneLandscape(w: number, h: number): boolean { return w >= h && h <= 420; }
/** 폰 가로 터치 조작(12칸 + 각성 게이지)의 전투 영역 점유율 상한 */
export const PHONE_BATTLE_BUDGET = 0.1;
/** 폰 가로 호의 버튼 지름 상한(화면 높이 비율) */
export const PHONE_ARC_FRAC = 0.125;

/**
 * 폰 가로의 작은 칸(물약 2·쪽 표시) 자리(C8-8 "물약 띠가 영웅 옆 하단 가운데까지 뻗는다"):
 *  ① 오른쪽 위 가장자리 줄(각성 게이지 왼쪽, 상단 띠 바로 아래)에 셋 다 들어가면 거기 — 하단 플레이 띠가 비고 미니맵 아래 가장자리에 붙는다.
 *  ② 위 줄에 둘 이상 들어가면 물약 둘은 위, 쪽 표시는 아래 줄(스킬3 왼쪽).
 *  ③ 아래 줄에 둘 이상이면 물약은 아래(엄지 쪽), 쪽 표시는 위.  ④ 아래 줄에 셋이면 예전 배치.
 * 각 줄의 칸은 오른쪽에서 왼쪽으로 2px씩 밀며 처음 맞는 자리를 고른다(고리·게이지·중앙 보호 영역·조이스틱 영역·10px 간격을 모두 지킴).
 */
function phoneSmallRow(base: TouchHudLayout, w: number, h: number, safe: HudInsets, protectedArea: HudRect, s3: HudRect, gauge: HudRect, small: number, gap: number): TouchHudLayout | null {
  const placed = [...base.skills, base.attack, base.dodge, base.awaken];
  const stickZone = baseJoystickZone(w, h);
  const ok = (r: HudRect, taken: readonly HudRect[]): boolean => {
    if (r.x < safe.left || r.y < safe.top + HUD_TOP_BAND || r.x + r.w > w - safe.right || r.y + r.h > h - safe.bottom) return false;
    if (hudRectsOverlap(r, protectedArea) || hudRectsOverlap(r, base.joystick) || hudRectsOverlap(r, stickZone) || hudRectsOverlap(r, gauge)) return false;
    for (const o of taken) if (circleGap(r, o) < gap - 1e-6) return false;
    return true;
  };
  const line = (startX: number, y: number): HudRect[] => {
    const out: HudRect[] = [];
    let x = startX - small;
    while (out.length < 3 && x >= safe.left) {
      const r = { x, y, w: small, h: small };
      if (ok(r, [...placed, ...out])) { out.push(r); x -= small + gap; } else x -= 2;
    }
    return out;
  };
  const up = line(gauge.x - gap, safe.top + HUD_TOP_BAND + 2);
  const down = line(s3.x - gap, base.attack.y + base.attack.h / 2 - small / 2);
  let potions: [HudRect, HudRect], page: HudRect;
  if (up.length >= 3) { potions = [up[0], up[1]]; page = up[2]; }
  else if (up.length >= 2 && down.length >= 1) { potions = [up[0], up[1]]; page = down[0]; }
  else if (down.length >= 2 && up.length >= 1) { potions = [down[0], down[1]]; page = up[0]; }
  else if (down.length >= 3) { potions = [down[0], down[1]]; page = down[2]; }
  else return null;
  const layout: TouchHudLayout = { ...base, potions, page };
  return touchArcFits(layout, w, h, safe, protectedArea) ? layout : null;
}

/** 원끼리 간격(중심 거리 − 두 반지름) */
export function circleGap(a: HudRect, b: HudRect): number {
  return Math.hypot(a.x + a.w / 2 - (b.x + b.w / 2), a.y + a.h / 2 - (b.y + b.h / 2)) - (a.w + b.w) / 2;
}

function touchArcFits(p: TouchHudLayout, w: number, h: number, safe: HudInsets, protectedArea: HudRect): boolean {
  const rects = [...p.skills, p.attack, p.dodge, p.awaken, p.page, ...p.potions];
  const stickZone = baseJoystickZone(w, h);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r.x < safe.left || r.y < safe.top + 112 || r.x + r.w > w - safe.right || r.y + r.h > h - safe.bottom) return false;
    if (hudRectsOverlap(r, protectedArea) || hudRectsOverlap(r, p.joystick) || hudRectsOverlap(r, stickZone) || hudRectsOverlap(r, p.gauge)) return false;
    for (let j = 0; j < i; j++) if (circleGap(r, rects[j]) < 10 - 1e-6) return false;
  }
  return !hudRectsOverlap(p.gauge, protectedArea) && p.gauge.y >= safe.top && p.gauge.x + p.gauge.w <= w - safe.right;
}

function padRect(r: HudRect, p: number): HudRect { return { x: r.x - p, y: r.y - p, w: r.w + p * 2, h: r.h + p * 2 }; }

/** 2열(폰 세로)·3열(작은 가로)의 오른쪽 격자. */
function touchGridLayout(w: number, h: number, scale: number, safe: HudInsets = NO_INSETS) {
  const cols = w < h ? 2 : 3;
  // 폰 가로 격자는 칸 사이 8px(C8-8 2회차: 640×360 오른쪽 기둥 높이를 줄인다. 44px 칸·서로 겹침 0은 그대로)
  const gap = phoneLandscape(w, h) ? 8 : 10, margin = 12;
  const usable = w / 3 - safe.right - margin * 2;
  // 폰 가로는 호와 같은 지름 상한(화면 높이 12.5%, C8-8)
  const cap = phoneLandscape(w, h) ? Math.max(44, h * PHONE_ARC_FRAC) : Infinity;
  const d = Math.max(44, Math.min(cap, 62 * Math.max(0.8, scale), (usable - (cols - 1) * gap) / cols, (h - safe.top - safe.bottom - 112 - margin * 2 - (12 / cols - 1) * gap) / (12 / cols)));
  const width = d * cols + gap * (cols - 1);
  const rows = 12 / cols;
  const left = w - safe.right - margin - width;
  const top = h - safe.bottom - margin - (d * rows + gap * (rows - 1));
  const cell = (i: number): HudRect => ({ x: left + (i % cols) * (d + gap), y: top + Math.floor(i / cols) * (d + gap), w: d, h: d });
  const skillStart = cols === 2 ? 4 : 3;
  return {
    size: d, skills: Array.from({ length: 6 }, (_, i) => cell(skillStart + i)),
    page: cell(cols === 2 ? 2 : 0), potions: [cell(cols === 2 ? 0 : 1), cell(cols === 2 ? 1 : 2)],
    dodge: cell(cols === 2 ? 3 : 9), awaken: cell(cols === 2 ? 10 : 11), attack: cell(cols === 2 ? 11 : 10),
    joystick: joystickRect(w, h, safe, margin),
    gauge: { x: left, y: top - 30, w: width, h: 24 },
  };
}

/** 서랍은 확대 설정을 반영하되 실제 44px 조작을 축소하지 않는다. 폭만 제약하고 목록 높이는 flex가 나눈다. */
export function skillDrawerWidth(w: number, uiFactor: number): number {
  return Math.min(w - 16, 400 * Math.max(0.9, Math.min(1.15, uiFactor)));
}

/**
 * 고객 배치(UX-FLOW 3절 "터치 버튼 조절"): 버튼마다 화면 비율 좌표(0..1)로 중심을 저장한다. 화면 크기·방향이 바뀌어도 비율로 따라가고,
 * 옮긴 자리가 화면 밖이면 안쪽으로 당기며, 다른 버튼·플로팅 조이스틱 입력 영역과 겹치면 그 버튼만 자동 배치로 되돌린다.
 */
export const TOUCH_CUSTOM_IDS = ['attack', 'dodge', 'awaken', 'page', 'potion0', 'potion1', 'skill0', 'skill1', 'skill2', 'skill3', 'skill4', 'skill5'] as const;
export type TouchCustomId = (typeof TOUCH_CUSTOM_IDS)[number];
export type TouchCustomPos = Partial<Record<TouchCustomId, { x: number; y: number }>>;

/** 배치에서 id의 사각형 */
export function touchRectOf(p: TouchHudLayout, id: TouchCustomId): HudRect {
  switch (id) {
    case 'attack': return p.attack;
    case 'dodge': return p.dodge;
    case 'awaken': return p.awaken;
    case 'page': return p.page;
    case 'potion0': return p.potions[0];
    case 'potion1': return p.potions[1];
    default: return p.skills[Number(id.slice(5))];
  }
}

/** 배치에서 id 칸 하나만 바꾼 사본 */
export function touchWithRect(p: TouchHudLayout, id: TouchCustomId, r: HudRect): TouchHudLayout {
  switch (id) {
    case 'attack': return { ...p, attack: r };
    case 'dodge': return { ...p, dodge: r };
    case 'awaken': return { ...p, awaken: r };
    case 'page': return { ...p, page: r };
    case 'potion0': return { ...p, potions: [r, p.potions[1]] };
    case 'potion1': return { ...p, potions: [p.potions[0], r] };
    default: { const skills = p.skills.slice(); skills[Number(id.slice(5))] = r; return { ...p, skills }; }
  }
}

/** 중심 비율 좌표를 버튼 크기 그대로 화면(안전영역) 안 사각형으로 */
export function touchRectAt(r: HudRect, cx: number, cy: number, w: number, h: number, safe: HudInsets = NO_INSETS): HudRect {
  const x = Math.min(Math.max(cx * w - r.w / 2, safe.left), w - safe.right - r.w);
  const y = Math.min(Math.max(cy * h - r.h / 2, safe.top), h - safe.bottom - r.h);
  return { x, y, w: r.w, h: r.h };
}

/**
 * 고정 HUD의 실제 사각형: 오른쪽 위 묶음(메뉴 아이콘 줄·골드·큰 미니맵·지역 명판·카메라 초기화, hudMenuLayout — 마을(접힘·펼침)·던전 모두)과 왼쪽 위 목표 카드·자동 버튼.
 * hud.ts가 같은 함수(hudMenuLayout·objectiveHudLayout·dungeonAutoButtonLayout)로 배치하므로 태블릿처럼 112px 띠 아래로 내려온 미니맵·카메라 단추도 잡힌다.
 * 고객이 버튼을 이 위로 옮기면 터치만 쓰는 고객이 판 중에 메뉴·설정을 누를 수 없다. u는 예전 호출 호환용(배치는 화면 크기만 본다).
 */
export function fixedHudRects(w: number, h: number, _u = 1): HudRect[] {
  const obj = objectiveHudLayout(w);
  const out: HudRect[] = [obj, dungeonAutoButtonLayout(obj.y + obj.h)];
  for (const scene of ['village', 'dungeon'] as const) for (const expanded of [false, true]) out.push(...hudMenuRects(hudMenuLayout(w, h, scene, { touch: true, expanded })));
  return out;
}

/**
 * 고객 배치 버튼을 둘 수 없는 고정 영역: 이동 조이스틱 입력 영역, 상단 HUD 띠(자동 배치와 같은 safe.top+112px), 각성 게이지,
 * 그리고 fixed로 넘긴 고정 HUD 사각형(fixedHudRects). 일시정지·카메라를 덮으면 터치만 쓰는 고객이 판 중에 그 버튼을 누를 수 없다.
 */
export function touchCustomReserved(p: TouchHudLayout, w: number, h: number, safe: HudInsets = NO_INSETS, fixed: readonly HudRect[] = []): HudRect[] {
  return [baseJoystickZone(w, h), { x: 0, y: 0, w, h: safe.top + 112 }, p.gauge, ...fixed];
}

/** id 칸이 고정 영역이나 다른 칸과 겹치는가(편집 중 빨간 표시·놓았을 때 되돌리기에 쓴다). ignore 칸은 장애물로 보지 않는다 */
export function touchCustomBlocked(p: TouchHudLayout, id: TouchCustomId, w: number, h: number, safe: HudInsets = NO_INSETS, ignore?: TouchCustomId, fixed: readonly HudRect[] = []): boolean {
  const r = touchRectOf(p, id);
  for (const z of touchCustomReserved(p, w, h, safe, fixed)) if (hudRectsOverlap(r, z)) return true;
  // 버튼끼리는 원(뿌리 border-radius 50%)으로 잰다: 고리 배치의 대각 이웃은 바깥 사각형이 겹쳐도 원은 떨어져 있다
  for (const o of TOUCH_CUSTOM_IDS) if (o !== id && o !== ignore && circleGap(r, touchRectOf(p, o)) < 0) return true;
  return false;
}

/**
 * 자동 배치 위에 고객 배치를 덮는다. 저장된 자리를 모두 먼저 놓은 완성 배치에서 겹침을 따지고,
 * 겹치는 버튼만 자동 자리로 되돌리며 더 이상 겹침이 없을 때까지 반복한다(id 순서에 따라 결과가 바뀌지 않게).
 * 버튼마다 많아야 한 번 되돌리므로 끝난다. 되돌린 자동 자리끼리는 자동 배치가 이미 겹치지 않는다.
 * ignore 칸은 놓지도 따지지도 않는다(편집기에서 끄는 칸: 나머지 배치를 그 칸과 무관하게 정한 뒤 끄는 자리를 그 위에서 판정한다).
 */
export function applyTouchCustom(p: TouchHudLayout, pos: TouchCustomPos, w: number, h: number, safe: HudInsets = NO_INSETS, ignore?: TouchCustomId, fixed: readonly HudRect[] = []): TouchHudLayout {
  let out = p;
  const live = new Set<TouchCustomId>();
  for (const id of TOUCH_CUSTOM_IDS) {
    const c = pos[id];
    if (!c || id === ignore) continue;
    out = touchWithRect(out, id, touchRectAt(touchRectOf(p, id), c.x, c.y, w, h, safe));
    live.add(id);
  }
  for (let changed = live.size > 0; changed;) {
    changed = false;
    for (const id of live) {
      if (!touchCustomBlocked(out, id, w, h, safe, ignore, fixed)) continue;
      out = touchWithRect(out, id, touchRectOf(p, id));
      live.delete(id); changed = true;
    }
  }
  return out;
}

/** 저장값 검증: 아는 id, 0..1 유한 좌표만 남긴다 */
export function sanitizeTouchPos(raw: unknown): TouchCustomPos {
  const out: TouchCustomPos = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const id of TOUCH_CUSTOM_IDS) {
    const v = (raw as Record<string, unknown>)[id] as { x?: unknown; y?: unknown } | undefined;
    if (v && typeof v.x === 'number' && typeof v.y === 'number' && Number.isFinite(v.x) && Number.isFinite(v.y) && v.x >= 0 && v.x <= 1 && v.y >= 0 && v.y <= 1) out[id] = { x: v.x, y: v.y };
  }
  return out;
}

/** 연계 안내 줄(linkHint) 높이와 기본 아래 여백(터치는 물약 줄, PC는 스킬 바 위) */
export const LINK_HINT_H = 26;
export function linkHintBottom(touch: boolean): number { return touch ? 98 : 116; }

/**
 * 연계 안내 줄 자리(2026-10-27 배포 확인: 568×320 던전·자동 이동·콤보에서 가운데로 올라온 자동 이동 알약 y118~215가 기본 자리 y196~222를 덮었다).
 * 기본은 아래 가운데(조작 줄 바로 위). 보이는 알약·요령(obstacles)과 겹치면 그 위·아래 후보 중 기본 자리에 가장 가까운 곳으로 옮긴다.
 * 위로는 상단 HUD 띠 아래까지, 아래로는 세로 화면이면 기본 자리까지(아래 조작 줄), 가로 화면이면 화면 아래 여백까지.
 * 둘 곳이 없으면 visible=false(안내 줄은 첫 3단 연계 전까지만 보이는 요령이라 잠깐 접어도 된다).
 */
export function linkHintLayout(w: number, h: number, touch: boolean, hintW: number, obstacles: readonly HudRect[]) {
  // 레이아웃이 바뀔 때만 부른다(LinkHint.place가 같은 사각형이면 건너뜀). 크기 0인 사각형은 숨은 것으로 본다
  const x = Math.round((w - hintW) / 2), base = h - linkHintBottom(touch) - LINK_HINT_H;
  const lo = Math.min(HUD_TOP_BAND + EDGE, base), hi = Math.max(h > w ? base : h - EDGE - LINK_HINT_H, base);
  let best = -1;
  for (let i = -1; i < obstacles.length * 2; i++) {
    const o = i < 0 ? null : obstacles[i >> 1];
    if (o && !(o.w > 0 && o.h > 0)) continue;
    const y = !o ? base : i & 1 ? Math.floor(o.y - EDGE - LINK_HINT_H) : Math.ceil(o.y + o.h + EDGE);
    if (y < lo || y > hi || (best >= 0 && Math.abs(y - base) >= Math.abs(best - base))) continue;
    let hit = false;
    for (let j = 0; j < obstacles.length && !hit; j++) {
      const b = obstacles[j];
      hit = b.w > 0 && b.h > 0 && x < b.x + b.w && x + hintW > b.x && y < b.y + b.h && y + LINK_HINT_H > b.y;
    }
    if (!hit) best = y;
  }
  return { x, y: best < 0 ? base : best, w: hintW, h: LINK_HINT_H, visible: best >= 0, moved: best >= 0 && best !== base };
}

/**
 * 조이스틱 옅은 고리(터치 대기 표시)의 가운데. 기본 자리는 (minX, h - bottom - r)인데 고정 HUD(「자동」 단추 등)와 겹치면
 * 고리를 오른쪽으로 비켜 놓는다(최대 maxX까지). 그래도 겹치면 null(그 화면에서는 고리를 숨긴다).
 * 고리는 "엄지를 여기 두세요" 힌트일 뿐이고 입력 영역은 그대로다(360x640 폰 던전: 고리 ↔ hud-auto 672px² 겹침 QA 2026-11-03).
 */
export function idleRingCenter(minX: number, maxX: number, h: number, r: number, bottom: number, fixed: readonly HudRect[]): { x: number; y: number } | null {
  const y = h - r - bottom;
  let x = minX;
  const at = (cx: number): HudRect => ({ x: cx - r, y: y - r, w: r * 2, h: r * 2 });
  for (let i = 0; i < fixed.length + 1; i++) {
    const hit = fixed.find((f) => hudRectsOverlap(at(x), f));
    if (!hit) return { x, y };
    x = hit.x + hit.w + r + 4;
    if (x > maxX) return null;
  }
  return null;
}
